import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard, TabBar, Th } from '../platform/_ui';
import { orgStorageApi } from '../api/org-storage.api.js';

const TABS = [
  { id: 'overview',  label: 'Overview',        icon: 'PieChart'  },
  { id: 'workspaces',label: 'Workspace Usage',  icon: 'LayoutGrid'},
  { id: 'files',     label: 'Largest Files',   icon: 'File'      },
  { id: 'uploads',   label: 'Recent Uploads',  icon: 'Upload'    },
  { id: 'quota',     label: 'Quota',           icon: 'Settings'  },
];

const FILE_TYPES = [
  { type: 'Documents', ext: '.pdf, .doc, .xlsx', icon: 'FileText',  color: 'var(--nx-accent)' },
  { type: 'Images',    ext: '.jpg, .png, .svg',  icon: 'Image',     color: 'var(--nx-accent)' },
  { type: 'Videos',    ext: '.mp4, .mov',         icon: 'Film',      color: 'var(--nx-accent)' },
  { type: 'Audio',     ext: '.mp3, .wav',         icon: 'Music',     color: 'var(--nx-accent)' },
  { type: 'Other',     ext: 'Archives, etc.',     icon: 'Archive',   color: '#8B949E' },
];

const pctBar = (pct, color) => (
  <div style={{ height: 6, borderRadius: 3, background: 'var(--nx-border)', marginTop: 6 }}>
    <div style={{ height: 6, borderRadius: 3, width: pct + '%', background: pct >= 90 ? 'var(--nx-danger)' : pct >= 70 ? 'var(--nx-warning)' : color, transition: 'width 0.4s' }} />
  </div>
);

const fmtSize = (bytes) => {
  if (!bytes) return '0 B';
  const gb = bytes / 1073741824;
  if (gb >= 1) return gb.toFixed(2) + ' GB';
  const mb = bytes / 1048576;
  if (mb >= 1) return mb.toFixed(1) + ' MB';
  return Math.round(bytes / 1024) + ' KB';
};

export default function OrgStorage({ data }) {
  const { orgId }  = useParams();
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel') || 'Back';
  const toast = useToast();

  const [tab, setTab]           = useState('overview');
  const [stats, setStats]       = useState(null);
  const [wsBkd, setWsBkd]       = useState([]);
  const [largestFiles, setLargestFiles] = useState([]);
  const [recentUploads, setRecentUploads] = useState([]);
  const [quota, setQuota]       = useState(null);
  const [quotaEdit, setQuotaEdit] = useState(false);
  const [quotaForm, setQuotaForm] = useState({ total: '100', unit: 'GB' });

  const load = useCallback(async () => {
    try {
      const [s, q] = await Promise.all([orgStorageApi.stats(orgId), orgStorageApi.getQuota(orgId)]);
      setStats(s);
      setQuota(q);
      if (q) setQuotaForm({ total: String(Math.round((q.totalBytes ?? 0) / 1073741824) || 100), unit: 'GB' });
    } catch { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load storage data' }); }
  }, [orgId]);

  const loadTab = useCallback(async () => {
    try {
      if (tab === 'workspaces') setWsBkd(((await orgStorageApi.workspaceBreakdown(orgId)) || []).sort((a, b) => Number(b.usedBytes || 0) - Number(a.usedBytes || 0)));
      if (tab === 'files')     setLargestFiles((await orgStorageApi.largestFiles(orgId)) || []);
      if (tab === 'uploads')   setRecentUploads((await orgStorageApi.recentUploads(orgId)) || []);
    } catch { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load storage data' }); }
  }, [orgId, tab]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTab(); }, [loadTab]);

  const org = data.state.organizations.find(o => o.id === orgId);
  if (!org) return null;

  const totalGB  = Math.round(Number(quota?.totalBytes ?? 0) / 1073741824) || 100;
  const usedGB   = Number(stats?.usedBytes ?? 0) / 1073741824;
  const usedPct  = totalGB > 0 ? Math.round((usedGB / totalGB) * 100) : 0;

  const handleSaveQuota = async () => {
    const totalGb = quotaForm.unit === 'TB' ? Number(quotaForm.total) * 1024 : Number(quotaForm.total);
    if (totalGb <= 0) { toast({ tone: 'warning', icon: 'AlertCircle', title: 'Quota must be greater than 0' }); return; }
    try {
      await orgStorageApi.updateQuota(orgId, { totalBytes: totalGb * 1073741824 });
      await load();
      setQuotaEdit(false);
      toast({ tone: 'success', icon: 'Check', title: 'Quota updated' });
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to update quota', desc: e.message });
    }
  };

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Storage"
        sub={'File storage management for ' + org.name}
        back={returnTo ? { label: `Back to ${returnLabel}`, onClick: () => navigate(returnTo) } : undefined}
        actions={<Btn variant="ghost" icon="Settings" onClick={() => setTab('quota')}>Manage Quota</Btn>}
      />

      <div className="nx-cc-stat-grid nx-kpi-4">
        <StatCard label="Total Storage" value={totalGB + ' GB'}           icon="HardDrive"  color="blue"   />
        <StatCard label="Used"          value={fmtSize(stats?.usedBytes)} icon="Archive"    color="amber"  />
        <StatCard label="Available"     value={(totalGB - usedGB).toFixed(1) + ' GB'} icon="Check" color="blue"  />
        <StatCard label="Files"         value={stats?.fileCount ?? 0}     icon="File"       color="purple" />
      </div>

      <TabBar tabs={TABS} active={tab} onChange={setTab} />
      <div className="nx-cc-tab-body">

        {tab === 'overview' && (
          <div>
            <div className="nx-cc-chart-card" style={{ marginBottom: 16 }}>
              <div className="nx-cc-chart-head">
                <span className="nx-cc-chart-title">Total Storage Usage</span>
                <span className="nx-muted nx-mini">{usedGB.toFixed(2)} GB of {totalGB} GB used</span>
              </div>
              <div style={{ marginTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12 }}>
                  <span style={{ color: 'var(--nx-text-muted)' }}>{usedPct}% used</span>
                  <span style={{ color: 'var(--nx-text-muted)' }}>{(totalGB - usedGB).toFixed(1)} GB free</span>
                </div>
                <div style={{ height: 12, borderRadius: 6, background: 'var(--nx-border)' }}>
                  <div style={{ height: 12, borderRadius: 6, width: usedPct + '%', background: usedPct >= 90 ? 'var(--nx-danger)' : usedPct >= 70 ? 'var(--nx-warning)' : 'var(--nx-accent)', transition: 'width 0.4s', minWidth: usedPct > 0 ? 12 : 0 }} />
                </div>
              </div>
            </div>

            <div className="nx-cc-chart-card">
              <div className="nx-cc-chart-head">
                <span className="nx-cc-chart-title">Storage by File Type</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px,1fr))', gap: 14, marginTop: 14 }}>
                {FILE_TYPES.map(ft => {
                  const entry = stats?.byType?.[ft.type] || {};
                  const pct = entry.pct ?? 0;
                  return (
                    <div key={ft.type} style={{ padding: '12px', borderRadius: 8, background: 'var(--nx-bg-alt,rgba(0,0,0,0.03))' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 6, background: ft.color + '18', color: ft.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <NexusIcon name={ft.icon} size={14} />
                        </div>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600 }}>{ft.type}</div>
                          <div style={{ fontSize: 10, color: 'var(--nx-text-muted)' }}>{ft.ext}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--nx-text-muted)' }}>
                        <span>{fmtSize(entry.bytes)}</span><span>{pct}%</span>
                      </div>
                      {pctBar(pct, ft.color)}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {tab === 'workspaces' && (
          <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
            <thead><tr>
              <Th icon="LayoutGrid">Workspace</Th>
              <Th icon="HardDrive">Used</Th>
              <Th icon="FileText">Files</Th>
              <Th icon="Activity">Usage %</Th>
            </tr></thead>
            <tbody>
              {wsBkd.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: '24px 0', color: 'var(--nx-text-muted)', fontSize: 13 }}>No workspace data available.</td></tr>
              ) : wsBkd.map(w => {
                const wsUsedGB = (w.usedBytes || 0) / 1073741824;
                const pct = totalGB > 0 ? Math.round((wsUsedGB / totalGB) * 100) : 0;
                return (
                  <tr key={w.workspaceId} className="nx-tr">
                    <td>
                      <span className="nx-row-name">
                        <span className="nx-domain-mark" style={{ width: 24, height: 24, fontSize: 10, background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)' }}>{(w.workspaceName || 'W')[0]}</span>
                        <span className="nx-row-name-text">{w.workspaceName}</span>
                      </span>
                    </td>
                    <td><span className="nx-muted">{fmtSize(w.usedBytes)}</span></td>
                    <td><span className="nx-muted">{w.fileCount ?? 0}</span></td>
                    <td style={{ width: 160 }}>
                      <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 3 }}>{pct}%</div>
                      {pctBar(pct, 'var(--nx-accent)')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div></div>
        )}

        {tab === 'files' && (
          largestFiles.length === 0 ? (
            <div className="nx-empty" style={{ flexDirection: 'column', gap: 6 }}>
              <NexusIcon name="File" size={22} />
              <span style={{ fontWeight: 600 }}>Largest Files</span>
              <span className="nx-muted nx-mini">No files found in this organization.</span>
            </div>
          ) : (
            <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
              <thead><tr>
                <Th icon="File">File</Th>
                <Th icon="HardDrive">Size</Th>
                <Th icon="LayoutGrid">Workspace</Th>
                <Th icon="Calendar">Uploaded</Th>
              </tr></thead>
              <tbody>{largestFiles.map(f => (
                <tr key={f.id} className="nx-tr">
                  <td><span className="nx-row-name-text">{f.name}</span></td>
                  <td><span className="nx-muted">{fmtSize(f.fileSize)}</span></td>
                  <td><span className="nx-muted">{f.workspaceName || '—'}</span></td>
                  <td><span className="nx-muted nx-mini">{f.createdAt ? new Date(f.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</span></td>
                </tr>
              ))}</tbody>
            </table></div></div>
          )
        )}

        {tab === 'uploads' && (
          recentUploads.length === 0 ? (
            <div className="nx-empty" style={{ flexDirection: 'column', gap: 6 }}>
              <NexusIcon name="Upload" size={22} />
              <span style={{ fontWeight: 600 }}>Recent Uploads</span>
              <span className="nx-muted nx-mini">No recent uploads found.</span>
            </div>
          ) : (
            <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
              <thead><tr>
                <Th icon="File">File</Th>
                <Th icon="HardDrive">Size</Th>
                <Th icon="User">Uploaded By</Th>
                <Th icon="Calendar">Date</Th>
              </tr></thead>
              <tbody>{recentUploads.map(f => (
                <tr key={f.id} className="nx-tr">
                  <td><span className="nx-row-name-text">{f.name}</span></td>
                  <td><span className="nx-muted">{fmtSize(f.fileSize)}</span></td>
                  <td><span className="nx-muted">{f.uploadedBy || '—'}</span></td>
                  <td><span className="nx-muted nx-mini">{f.createdAt ? new Date(f.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</span></td>
                </tr>
              ))}</tbody>
            </table></div></div>
          )
        )}

        {tab === 'quota' && (
          <div style={{ maxWidth: 600 }}>
            <div className="nx-cc-chart-card" style={{ marginBottom: 14 }}>
              <div className="nx-cc-chart-head">
                <span className="nx-cc-chart-title">Storage Quota</span>
                {!quotaEdit && <Btn variant="ghost" size="sm" icon="Pencil" onClick={() => setQuotaEdit(true)}>Edit</Btn>}
              </div>
              {!quotaEdit ? (
                <div className="nx-form" style={{ marginTop: 10 }}>
                  {[
                    ['Total Quota', quotaForm.total + ' ' + quotaForm.unit],
                    ['Used', fmtSize(stats?.usedBytes)],
                    ['Available', (Number(quotaForm.total) - usedGB).toFixed(1) + ' ' + quotaForm.unit],
                    ['Policy', 'Hard limit — uploads blocked when quota exceeded'],
                  ].map(([label, value]) => (
                    <div key={label} className="nx-form-row"><label>{label}</label><span>{value}</span></div>
                  ))}
                </div>
              ) : (
                <div className="nx-form" style={{ marginTop: 10 }}>
                  <div className="nx-form-row">
                    <label>Total Quota</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input className="nx-input" type="number" min="1" value={quotaForm.total} onChange={e => setQuotaForm(f => ({ ...f, total: e.target.value }))} style={{ width: 120 }} />
                      <select className="nx-input" value={quotaForm.unit} onChange={e => setQuotaForm(f => ({ ...f, unit: e.target.value }))} style={{ width: 100 }}>
                        <option>GB</option><option>TB</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <Btn variant="ghost" onClick={() => setQuotaEdit(false)}>Cancel</Btn>
                    <Btn variant="primary" icon="Check" onClick={handleSaveQuota}>Save Quota</Btn>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
